import React from 'react';

import { Gateway } from 'types/gateway';

import { CreateInputStreamsForm } from 'streams/components/CreateInputStreamsDialog/CreateInputStreamsForm';
import { Drawer, DrawerProps } from 'components/Drawer/DrawerDeprecated';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Radio, RadioOption } from 'components/Radio/Radio';
import { StreamTypeInfo } from 'streams/components/StreamTypeInfo/StreamTypeInfo';
import { Text } from 'components/Text/Text';

import { ImportFromSource } from './ImportFromSource/ImportFromSource';
import { SourceType } from './ImportFromSource/types';

export type CreateInputStreamsDialogProps = DrawerProps & {
  gateway?: Gateway;
  onSuccess?: () => void;
};

type Source = SourceType | 'manual';

const SOURCE_OPTIONS: { value: Source; label: string }[] = [
  { value: 'manual', label: 'Stream URL' },
  { value: 'avigilon', label: 'Avigilon' },
  { value: 'hanwhawave', label: 'Hanwha Wave Sync' },
  { value: 'rhombus', label: 'Rhombus' },
  { value: 'verkada', label: 'Verkada' },
];

export function CreateInputStreamsDialog({
  open,
  gateway,
  onSuccess,
  onClose,
}: CreateInputStreamsDialogProps) {
  const [selectedSource, setSelectedSource] = React.useState<Source>('manual');

  // Reset selection when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setSelectedSource('manual');
    }
  }, [open]);

  function handleClose() {
    setSelectedSource('manual');
    onClose?.();
  }

  function handleSuccess() {
    setSelectedSource('manual');
    onClose?.();
    onSuccess?.();
  }

  return (
    <Drawer
      title="Add live streams"
      open={open}
      onClose={handleClose}
      classNames={{ content: '[--box-my:var(--size-md)]' }}
    >
      <Text type="paragraph">
        Add streams to Lumeo from a third-party VMS or by providing RTSP/HTTP
        URLs directly.
      </Text>
      <ExternalLink href="https://docs.lumeo.com/docs/camera-setup-guide#stream-setup">
        Stream setup guide
      </ExternalLink>

      <div className="mt-4">
        <Radio
          id="stream-source"
          value={selectedSource}
          onChange={(value) => setSelectedSource(value as Source)}
        >
          {SOURCE_OPTIONS.map((source) => (
            <RadioOption key={source.value} value={source.value}>
              {source.label}
            </RadioOption>
          ))}
        </Radio>
      </div>

      {selectedSource === 'manual' && (
        <div className="mt-4">
          <StreamTypeInfo gateway={gateway} onClose={handleClose} />
          <CreateInputStreamsForm
            gateway={gateway}
            onSuccess={handleSuccess}
          />
        </div>
      )}

      {selectedSource !== 'manual' && (
        <div className="mt-4">
          <ImportFromSource
            key={selectedSource}
            sourceType={selectedSource}
            gateway={gateway}
            onSuccess={handleSuccess}
          />
        </div>
      )}
    </Drawer>
  );
}
